import Layout from './layout.js';
import Direction from './direction.js';
import LabelRepel1D from './labelRepel1D.js';
import { Side, SideParams } from './side.js';

import {
    BaseSidesLayout,
    getSizeLnKey, getSizeTrKey, getPosLnKey, getPosTrKey, getPosLnIndex, getPosTrIndex,
} from './baseSidesLayout.js';

class SidesLayout extends Layout {
    constructor({ direction }) {
        super();

        this._direction = direction;
        this._labels = new Set();
        this._baseLayout = new BaseSidesLayout({ direction });

        this._mask = null;

        this._labelRepel = [];
        for (let i = 0; i < 2; i++) {
            this._labelRepel.push(new LabelRepel1D());
        }
    }

    addLabel(id) {
        this._baseLayout.addLabel(id);
        this._labels.add(id);
    }

    removeLabel(id) {
        this._baseLayout.removeLabel(id);
        for (let labelRepel of this._labelRepel) {
            labelRepel.removeLabel(id);
            labelRepel.removeLabelBox(id);
        }
        this._labels.delete(id);
    }

    setLabel(id, data) {
        if ('pointPosition' in data || 'labelPosition' in data || 'farSide' in data) {
            this._baseLayout.setLabel(id, data);
        }

        if ('size' in data) {
            const { size } = data;

            if (size) {
                const dir = this._direction;
                const sizeTrKey = getSizeTrKey(dir);

                const sizeTr = size[sizeTrKey];
                const box = { min: -sizeTr*0.5, max: sizeTr*0.5 };

                for (let labelRepel of this._labelRepel) {
                    labelRepel.setLabelBox(id, box);
                }
            } else {
                for (let labelRepel of this._labelRepel) {
                    labelRepel.removeLabelBox(id);
                }
            }
        }
    }

    setMask(mask) {
        this._mask = mask;
        this._baseLayout.setMask(mask);
    }

    getLabel(id) {
        return this._baseLayout.getLabel(id);
    }

    update(useMask) {
        const dir = this._direction;
        const sizeLnKey = getSizeLnKey(dir), sizeTrKey = getSizeTrKey(dir);
        const posLnKey = getPosLnKey(dir), posTrKey = getPosTrKey(dir);
        const posLnIndex = getPosLnIndex(dir), posTrIndex = getPosTrIndex(dir);



        this._baseLayout.update(useMask);

        for (let id of this._labels) {
            const data = this._baseLayout.getLabel(id);

            for (let [farSideInt, labelRepel] of this._labelRepel.entries()) {
                if (data && Boolean(farSideInt) === data.farSide) {
                    labelRepel.setLabelPosition(id, data.labelPosition[posTrKey]);
                } else {
                    labelRepel.removeLabel(id);
                }
            }
        }



        const repelStep = this._repelStep;
        const repelMaxIterations = this._repelMaxIterations;

        for (let farSideInt = 0; farSideInt < 2; farSideInt++) {
            const farSide = Boolean(farSideInt);
            const labelRepel = this._labelRepel[farSideInt];

            if (useMask) {
                const sizeTr = this._mask[sizeTrKey];

                if (labelRepel.calculate()) {
                    const labelClampedPush = new Map();
                    const unclampedPositions = new Map();
                    const labelOrder = [];

                    for (let id of this._labels) {
                        const data = this._baseLayout.getLabel(id);

                        if (data !== null && data.farSide === farSide) {
                            const { pointPosition: { [posTrKey]: pointTr } } = data;
                            let push = labelRepel.getLabelPush(id);

                            unclampedPositions.set(id, pointTr+push);
                            labelOrder.push(id);

                            let clampedPush = push;

                            if (push !== 0) {
                                const nearestPoints = this._baseLayout.regionLayerNearestPoints[farSideInt].nearestPoints.get(id);

                                if (nearestPoints) {
                                    let actualPush = 0;

                                    const pushSign = Math.sign(push);
                                    const pushMag = Math.abs(push);
                                    for (let offsetMag = 0; offsetMag <= pushMag; offsetMag++) {
                                        const clampedOffsetMag = Math.min(offsetMag, pushMag);
                                        const clampedOffset = clampedOffsetMag*pushSign;
                                        const newPointTr = pointTr+clampedOffset;
                                        const roundNewPointTr = Math.round(newPointTr);

                                        if (roundNewPointTr >= 0 && roundNewPointTr < sizeTr && nearestPoints.position.has(roundNewPointTr)) {
                                            clampedPush = clampedOffset;
                                        }
                                    }
                                }
                            }

                            labelClampedPush.set(id, clampedPush);
                        }
                    }

                    labelOrder.sort((id1, id2) => unclampedPositions.get(id1)-unclampedPositions.get(id2));

                    let rangeStart = 0;
                    while (rangeStart < labelOrder.length) {
                        let startPush = labelRepel.getLabelPush(labelOrder[rangeStart]);

                        let rangeEnd = rangeStart+1;
                        if (startPush !== 0) {
                            if (startPush < 0) {
                                while (rangeEnd < labelOrder.length && labelRepel.getLabelPush(labelOrder[rangeEnd]) < 0) {
                                    rangeEnd++;
                                }

                                if (rangeEnd < labelOrder.length && labelRepel.getLabelPush(labelOrder[rangeEnd]) === 0) {
                                    rangeEnd++;
                                }
                            }

                            while (rangeEnd < labelOrder.length && labelRepel.getLabelPush(labelOrder[rangeEnd]) > 0) {
                                rangeEnd++;
                            }

                            const count = rangeEnd-rangeStart;
                            if (count > 1) {
                                const firstId = labelOrder[rangeStart], lastId = labelOrder[rangeEnd-1];
                                const firstData = this._baseLayout.getLabel(firstId);
                                const lastData = this._baseLayout.getLabel(lastId);
                                const firstTr = firstData.labelPosition[posTrKey]+labelClampedPush.get(firstId);
                                const lastTr = lastData.labelPosition[posTrKey]+labelClampedPush.get(lastId);

                                for (let i = 0; i < count; i++) {
                                    const id = labelOrder[rangeStart+i];
                                    const data = this._baseLayout.getLabel(id);
                                    const { pointPosition, labelPosition } = data;
                                    const thisTr = firstTr+(lastTr-firstTr)*i/(count-1);

                                    labelPosition[posTrKey] = thisTr;
                                    pointPosition[posTrKey] = thisTr;
                                    this._baseLayout.setLabel(id, data);
                                    labelRepel.setLabelPosition(id, thisTr);
                                }
                            }
                        }

                        rangeStart = rangeEnd;
                    }
                }
            }

            if (labelRepel.calculate()) {
                for (let id of this._labels) {
                    const data = this._baseLayout.getLabel(id);

                    if (data !== null && data.farSide === farSide) {
                        const { labelPosition } = data;
                        let push = labelRepel.getLabelPush(id);
                        labelPosition[posTrKey] += push;
                        this._baseLayout.setLabel(id, data);
                        labelRepel.setLabelPosition(id, labelPosition[posTrKey]);
                    }
                }
            }
        }

        if (useMask) {
            for (let id of this._labels) {
                const data = this._baseLayout.getLabel(id);

                if (data !== null) {
                    const { labelPosition, pointPosition, farSide } = data;
                    const farSideInt = Number(farSide);

                    let nearestPoints = this._baseLayout.regionLayerNearestPoints[farSideInt].nearestPoints.get(id);
                    let pointTr = pointPosition[posTrKey];

                    if (nearestPoints.position.has(pointTr)) {
                        pointPosition[posLnKey] = nearestPoints.position.get(pointTr);
                    }

                    this._baseLayout.setLabel(id, data);
                }
            }
        }

    }

    // debug
    generateDistanceImageData() {
        return this._baseLayout.generateDistanceImageData();
    }
}

export default SidesLayout;
